import {BillingCycle, PlanTier, Subscription} from "../types/subscription"

export type {PlanTier}

export type Plan = {
    tier: PlanTier
    /** Name the plan is sold under, also the label shown wherever a tier is displayed */
    name: string
    /** Charged once, for the whole access period */
    price: number
    currency: string
    billingCycle: BillingCycle
    /** How long the access lasts, in days */
    durationDays: number
    /** Analyses granted per day, null when the plan does not cap them */
    dailyPredictions: number | null
    /**
     * Saving against paying the Standard week over the same period, as a percentage.
     * Only carried by the plans that beat it.
     */
    savingPercent?: number
    /** Keys of the feature lines, resolved against the translations of the pricing page */
    features: string[]
    /** The plan the pricing page pushes, one at most */
    recommended?: boolean
}

/**
 * The plans on sale.
 *
 * Mirrors backend/src/values/plans.ts, which is what actually grants the daily limit and the access
 * period once a payment goes through: prices and limits have to be changed in both.
 *
 * Analyses are paid for. An account that has never subscribed, or whose access has run out, sits on
 * the free tier, which grants none — it is a state, not a plan, and it is not sold here.
 */
export const PLANS: Plan[] = [{
    tier: "standard",
    name: "Standard",
    price: 2_500,
    currency: "XAF",
    billingCycle: "weekly",
    durationDays: 7,
    dailyPredictions: 2,
    features: ["standardFeaturePredictions", "standardFeatureAccess", "standardFeatureSupport"]
}, {
    tier: "premium",
    name: "Premium",
    price: 4_500,
    currency: "XAF",
    billingCycle: "biweekly",
    durationDays: 14,
    dailyPredictions: 5,
    savingPercent: 10,
    features: [
        "premiumFeatureEverythingStandard",
        "premiumFeaturePredictions",
        "premiumFeatureAccess",
        "premiumFeatureHistory",
        "premiumFeatureAlerts"
    ]
}, {
    tier: "platinum",
    name: "Platine",
    price: 8_000,
    currency: "XAF",
    billingCycle: "monthly",
    durationDays: 30,
    dailyPredictions: null,
    savingPercent: 20,
    features: [
        "platinumFeatureEverythingPremium",
        "platinumFeaturePredictions",
        "platinumFeatureVip",
        "platinumFeatureAccess",
        "platinumFeatureStats",
        "platinumFeatureOdds",
        "platinumFeatureSupport"
    ],
    recommended: true
}]

/**
 * Where an account stands before it has paid, and where it falls back once its access runs out.
 * It grants no analysis at all, so it is never shown as something to choose.
 */
export const FREE_PLAN: Plan = {
    tier: "free",
    name: "Découverte",
    price: 0,
    currency: "XAF",
    billingCycle: "monthly",
    durationDays: 0,
    dailyPredictions: 0,
    features: []
}

export const ALL_PLANS = [FREE_PLAN, ...PLANS]

export const planOf = (tier?: PlanTier) => ALL_PLANS.find(plan => plan.tier === tier) || FREE_PLAN

/** Tier names as shown in the app, so a subscription never reads as a raw tier key */
export const TIER_LABELS: { [tier in PlanTier]: string } = ALL_PLANS.reduce((labels, plan) => ({
    ...labels,
    [plan.tier]: plan.name
}), {} as { [tier in PlanTier]: string })

/** 2500 reads as "2 500", the grouping francs are usually written with */
export const formatAmount = (amount: number) => amount.toLocaleString("fr-FR")

/** An access is only worth something while it is active and inside its period */
export const isSubscriptionActive = (subscription?: Subscription) =>
    !!subscription && subscription.status === "active" && subscription.expiresAt > Date.now()

/**
 * Analyses already spent today.
 *
 * The counters are reset by the API the next time an analysis is spent, not at midnight, so an
 * account read before its first analysis of the day still carries yesterday's tally. Anything that
 * shows the figure has to discount it the same way, or it reports a quota that is not the one the
 * API will apply.
 */
export const usedAnalysesToday = (subscription?: Subscription) => {
    if (!subscription)
        return 0

    const sameDay = new Date(subscription.lastResetAt).toDateString() === new Date().toDateString()

    return sameDay ? subscription.predictionsUsedToday || 0 : 0
}

/** Analyses the account can still spend today */
export const remainingAnalyses = (subscription?: Subscription) =>
    isSubscriptionActive(subscription)
        ? Math.max(0, subscription.dailyPredictionsLimit - usedAnalysesToday(subscription))
        : 0
